<template>
  <div class="modal fade" :id="modalId" tabindex="-1" role="dialog" aria-labelledby="updateDateModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="updateDateModalLabel">
            Update Payment Start Date
          </h5>
          <button type="button" class="btn-close" data-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="unit" class="mb-3">
            <div class="alert alert-warning">
              <i class="mdi mdi-alert-circle-outline me-2"></i>
              <strong>Payment date not set!</strong><br>
              This unit requires a payment start date before billing can proceed.
            </div>

            <div class="unit-info mb-4">
              <h6>Unit Information:</h6>
              <table class="table table-sm">
                <tr>
                  <td><strong>Unit No:</strong></td>
                  <td>{{ unit.unitNo }}</td>
                </tr>
                <tr>
                  <td><strong>Occupant:</strong></td>
                  <td>{{ unit.occupants }}</td>
                </tr>
                <tr>
                  <td><strong>Monthly Rent:</strong></td>
                  <td>KES {{ numberFormat(unit.monthlyRent) }}</td>
                </tr>
                <tr>
                  <td><strong>Current Status:</strong></td>
                  <td>
                    <span class="badge" :class="unit.lastPaymentDate ? 'bg-success' : 'bg-danger'">
                      {{ unit.lastPaymentDate ? 'Has Payment Date' : 'No Payment Date' }}
                    </span>
                  </td>
                </tr>
              </table>
            </div>

            <form @submit.prevent="updatePaymentDate">
              <div class="row">
                <div class="col-12">
                  <label class="form-label required">Select Payment Start Date</label>
                  <p class="text-muted small">This date determines when rent calculation begins for this unit.</p>
                </div>

                <div class="col-md-6 mb-3">
                  <label for="dateType" class="form-label">Date Type</label>
                  <select v-model="dateType" class="form-select" id="dateType" @change="onDateTypeChange">
                    <option value="lastPayment">Last Payment Date</option>
                    <option value="moveIn">Move-in Date (Effective Date)</option>
                    <option value="custom">Custom Date</option>
                  </select>
                </div>

                <div class="col-md-6 mb-3">
                  <label for="paymentDate" class="form-label">
                    {{ dateType === 'lastPayment' ? 'Last Payment Date' : dateType === 'moveIn' ? 'Move-in Date' : 'Select Date' }}
                  </label>
                  <input
                      v-model="selectedDate"
                      type="date"
                      class="form-control"
                      id="paymentDate"
                      :max="maxDate"
                      required
                  >
                </div>

                <div class="col-12 mb-3">
                  <label for="notes" class="form-label">Notes (Optional)</label>
                  <textarea
                      v-model="notes"
                      class="form-control"
                      id="notes"
                      rows="2"
                      placeholder="Add any relevant notes about this date update..."
                  ></textarea>
                </div>

                <div class="col-12">
                  <div class="alert alert-info">
                    <i class="mdi mdi-information-outline me-2"></i>
                    <strong>Important:</strong>
                    <ul class="mb-0 mt-2">
                      <li>If this is the <strong>last payment date</strong>, rent will be calculated from the month after this date.</li>
                      <li>If this is the <strong>move-in date</strong>, rent will be calculated from this date.</li>
                      <li>This action will affect how arrears are calculated for this unit.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </form>
          </div>

          <div v-else class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="sr-only">Loading...</span>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-dismiss="modal">Cancel</button>
          <button
              type="button"
              class="btn btn-primary"
              @click="updatePaymentDate"
              :disabled="!selectedDate || updating"
          >
            <span v-if="updating">
              <span class="spinner-border spinner-border-sm me-2" role="status"></span>
              Updating...
            </span>
            <span v-else>
              <i class="mdi mdi-check me-2"></i>
              Update Date
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import { execute, rent } from "@/api";

export default {
  name: "UpdatePaymentDateModal",
  props: {
    unit: {
      type: Object,
      default: null
    },
    modalId: {
      type: String,
      default: "update-payment-date-modal"
    }
  },
  data() {
    return {
      dateType: 'lastPayment',
      selectedDate: '',
      notes: '',
      updating: false,
      maxDate: new Date().toISOString().split('T')[0]
    };
  },
  methods: {
    numberFormat(num) {
      const formatter = new Intl.NumberFormat("en-GB");
      return formatter.format(num || 0);
    },
    onDateTypeChange() {
      // Reset date when type changes
      this.selectedDate = '';

      // Set default date based on type
      if (this.dateType === 'moveIn') {
        // Default to first day of current month for move-in
        const now = new Date();
        this.selectedDate = new Date(now.getFullYear(), now.getMonth(), 1)
            .toISOString().split('T')[0];
      }
    },
    async updatePaymentDate() {
      if (!this.selectedDate || !this.unit) {
        alert("Please select a date");
        return;
      }

      this.updating = true;

      try {
        const data = new FormData();
        data.append("function", "updateUnitPaymentDate");
        data.append("unitID", this.unit.id);
        data.append("dateType", this.dateType);
        data.append("selectedDate", this.selectedDate);
        data.append("notes", this.notes);
        data.append("sessionNames", sessionStorage.getItem("sessionNames"));
        data.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));

        const response = await execute(data, rent);

        if (response.data.success) {
          alert("Payment date updated successfully!");

          // Close modal
          const modal = bootstrap.Modal.getInstance(document.getElementById(this.modalId));
          if (modal) modal.hide();

          // Emit success event
          this.$emit('date-updated', {
            unitID: this.unit.id,
            dateType: this.dateType,
            newDate: this.selectedDate
          });

          // Reset form
          this.selectedDate = '';
          this.notes = '';
          this.dateType = 'lastPayment';
        } else {
          alert(response.data.message || "Failed to update payment date");
        }
      } catch (error) {
        console.error("Error updating payment date:", error);
        alert("An error occurred while updating the payment date");
      } finally {
        this.updating = false;
      }
    },
    show() {
      // Reset form when showing
      this.selectedDate = '';
      this.notes = '';
      this.dateType = 'lastPayment';
    }
  }
};
</script>

<style scoped>
.required::after {
  content: " *";
  color: red;
}

.unit-info table td {
  padding: 0.25rem 0.5rem;
}

.alert ul {
  padding-left: 1.25rem;
}
</style>